/**
 * UpdatesTab Component
 *
 * Renders the Updates tab content for CompanyDetails, including:
 * - News cards with domain logo, title, date, and clickable image
 * - Pagination for navigating through news pages
 *
 * SELF-CONTAINED MODE: When currentPage/onPageChange are not provided,
 * the component manages its own pagination state internally.
 *
 * CONTROLLED MODE: When currentPage/onPageChange are provided,
 * pagination is controlled by parent (backwards compatible).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import Pagination from '../../../navigation/Pagination';
import { getCompanyNewsDisplayTime } from '../../../../pages/Outreach/desktop/constants';
import { getLogoUrl } from '../../../../utils/logo';

const DEFAULT_PAGE_SIZE = 8;

/**
 * @param {Object} props
 * @param {Array} props.news - Array of news items [{title, domain, date, url, imageUrl}]
 * @param {string} props.website - Company website URL (used for URL handling)
 * @param {number} [props.currentPage] - Current page (controlled mode, 1-indexed)
 * @param {number} [props.totalPages] - Total pages (controlled mode)
 * @param {boolean} [props.showPagination] - Show pagination (controlled mode)
 * @param {number} [props.pageSize] - Items per page (default: 8)
 * @param {Date} [props.now] - Current date for time formatting
 * @param {Function} [props.onPageChange] - Page change callback (controlled mode)
 * @param {Function} [props.onTabVisit] - Callback called once on mount (self-contained mode)
 */
const UpdatesTab = ({
  news = [],
  website = '',
  currentPage: controlledPage,
  totalPages: controlledTotalPages,
  showPagination: controlledShowPagination,
  pageSize = DEFAULT_PAGE_SIZE,
  now = new Date(),
  onPageChange,
  onTabVisit
}) => {
  // Internal state for self-contained mode
  const [internalPage, setInternalPage] = useState(1);

  // Track if onTabVisit has been called (to prevent multiple calls on re-renders)
  const hasCalledOnTabVisit = useRef(false);

  // Call onTabVisit once on mount
  useEffect(() => {
    if (onTabVisit && !hasCalledOnTabVisit.current) {
      hasCalledOnTabVisit.current = true;
      onTabVisit();
    }
  }, [onTabVisit]);

  // Determine if we're in controlled mode
  const isControlled = controlledPage !== undefined && onPageChange !== undefined;

  // Use controlled or internal values
  const currentPage = isControlled ? controlledPage : internalPage;

  const newsItems = news || [];

  // Compute pagination values (used in self-contained mode)
  const computedTotalPages = useMemo(
    () => Math.ceil(newsItems.length / pageSize),
    [newsItems.length, pageSize]
  );
  const computedShowPagination = computedTotalPages > 1;

  // Use controlled or computed values
  const totalPages = isControlled ? controlledTotalPages : computedTotalPages;
  const showPagination =
    controlledShowPagination !== undefined ? controlledShowPagination : computedShowPagination;

  const paginatedNews = newsItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCardClick = (entry) => {
    const url = website.startsWith('http') ? entry.url : 'https://' + entry.url;
    window.open(url, '_blank');
  };

  const handlePageChange = (selected) => {
    const newPage = selected + 1;

    if (isControlled) {
      // Controlled mode: call parent callback
      onPageChange(newPage);
    } else {
      // Self-contained mode: update internal state
      setInternalPage(newPage);
    }

    setTimeout(() => {
      const tabsDOM = document.querySelector('.tabs-wrap');
      if (tabsDOM) {
        tabsDOM.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  };

  return (
    <div className="product-portfolio-updates-wrap">
      <div className="news-container">
        <div className="news-main-title">Last updates</div>
        {paginatedNews.map((entry, idx) => {
          const displayTime = getCompanyNewsDisplayTime(entry.date, now);
          const logoUrl = getLogoUrl(entry.domain);
          return (
            <div
              className="news-card"
              key={idx}
              role="button"
              tabIndex={0}
              onClick={() => handleCardClick(entry)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardClick(entry);
                }
              }}
            >
              <div className="news-info">
                <div className="domain-wrap">
                  {logoUrl && (
                    <img
                      className="domain-logo"
                      src={logoUrl}
                    />
                  )}
                  <div className="domain-text">{entry.domain}</div>
                </div>
                <div className="news-title">{entry.title}</div>
                <div className="news-date">{displayTime}</div>
              </div>
              <div className="image-wrap">
                <img src={entry.imageUrl} />
              </div>
            </div>
          );
        })}
      </div>
      <Pagination
        show={showPagination}
        onPageChange={handlePageChange}
        page={currentPage - 1}
        pages={totalPages}
      />
    </div>
  );
};

export default UpdatesTab;
